/**
 * Per-slot channel mix (volume + pan) for the DAW-style strip / kernel.
 */
import type { InstrumentSlot, SlotMix } from "../types";
import { DEFAULT_INSTRUMENT_SLOT_COUNT, clampInstrumentSlotCount } from "../types";

export const DEFAULT_SLOT_VOLUME = 1;
export const DEFAULT_SLOT_PAN = 0;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function createDefaultSlotMix(): SlotMix {
  return { volume: DEFAULT_SLOT_VOLUME, pan: DEFAULT_SLOT_PAN };
}

export function createDefaultSlotMixes(
  count: number = DEFAULT_INSTRUMENT_SLOT_COUNT,
): SlotMix[] {
  const n = clampInstrumentSlotCount(count);
  return Array.from({ length: n }, () => createDefaultSlotMix());
}

export function normalizeSlotMix(raw: Partial<SlotMix> | undefined): SlotMix {
  const factory = createDefaultSlotMix();
  if (!raw || typeof raw !== "object") {
    return factory;
  }
  return {
    volume: clamp(Number(raw.volume ?? factory.volume), 0, 1),
    pan: clamp(Number(raw.pan ?? factory.pan), -1, 1),
  };
}

export function normalizeSlotMixes(
  raw: unknown,
  count: number = DEFAULT_INSTRUMENT_SLOT_COUNT,
): SlotMix[] {
  const n = clampInstrumentSlotCount(count);
  const list = Array.isArray(raw) ? raw : [];
  return Array.from({ length: n }, (_, i) =>
    normalizeSlotMix(list[i] as Partial<SlotMix> | undefined),
  );
}

export function cloneSlotMix(mix: SlotMix): SlotMix {
  return { volume: mix.volume, pan: mix.pan };
}

export function cloneSlotMixes(mixes: SlotMix[]): SlotMix[] {
  return mixes.map(cloneSlotMix);
}

/** Equal-power pan gains for pan in −1..1 (left..right). */
export function equalPowerPanGains(pan: number): { left: number; right: number } {
  const t = clamp(pan, -1, 1);
  const angle = (t + 1) * (Math.PI / 4); // 0..π/2
  return {
    left: Math.cos(angle),
    right: Math.sin(angle),
  };
}

export function patchSlotMix(
  mixes: SlotMix[],
  slot: InstrumentSlot,
  partial: Partial<SlotMix>,
): SlotMix[] {
  const n = Math.max(mixes.length, slot + 1);
  const next = cloneSlotMixes(normalizeSlotMixes(mixes, n));
  next[slot] = normalizeSlotMix({ ...next[slot], ...partial });
  return next;
}
